import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@common/components/DatePicker/DatePicker";

import { UseFormReturn } from "react-hook-form";
import { StipendExpense, StipendExpenseStatus } from "@common/utils/graphql";
import { StipendFormValues } from "../forms/Forms";
import { formatDollars, formatDate } from "@common/components/utils";
import { CircleNotch } from "@phosphor-icons/react";
import { Body } from "@hypercard/web-components";
import { CurrencyInput } from "@common/components/CurrencyInput";
import { ReceiptUploadDropzone } from "@common/components";
import { FilePreview } from "@/components/ui/file-preview";

interface Props {
  form: UseFormReturn<StipendFormValues>;
  onSubmit: (f: StipendFormValues) => void;
  viewOnly: boolean;
  expense?: StipendExpense;
  isSubmitting: boolean;
  maxStipendAmount?: number;
  onParseReceipt: (params: Partial<StipendFormValues>) => void;
}

export const CreateStipendForm = ({
  form,
  onSubmit,
  viewOnly,
  expense,
  isSubmitting,
  onParseReceipt,
}: Props) => {
  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-1 flex-col space-y-4"
      >
        {/* Receipt upload */}
        <FormField
          control={form.control}
          name="receipt"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Receipt</FormLabel>
              {viewOnly ? (
                <FilePreview url={expense!.receiptFileUrl} />
              ) : (
                <>
                  <FormControl>
                    <ReceiptUploadDropzone
                      onParseReceipt={onParseReceipt}
                      field={field}
                    />
                  </FormControl>
                  <FormMessage />
                </>
              )}
            </FormItem>
          )}
        />

        {/* Merchant */}
        <FormField
          control={form.control}
          name="merchant"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Merchant</FormLabel>
              {viewOnly ? (
                <div>{expense!.merchantName}</div>
              ) : (
                <FormControl>
                  <Input placeholder="Merchant" {...field} />
                </FormControl>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex space-x-4">
          {/* Amount */}
          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Amount</FormLabel>
                {viewOnly ? (
                  <div>{formatDollars(expense!.amount)}</div>
                ) : (
                  <FormControl className="w-32">
                    <CurrencyInput {...field} />
                  </FormControl>
                )}
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Date picker */}
          <FormField
            control={form.control}
            name="date"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>Transaction Date</FormLabel>
                {viewOnly ? (
                  <div>{formatDate(expense!.transactionDate)}</div>
                ) : (
                  <FormControl>
                    <DatePicker
                      selected={field.value}
                      onSelect={field.onChange}
                    />
                  </FormControl>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Notes */}
        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex justify-between">
                <div>Notes</div>
                {!viewOnly && <div>Optional</div>}
              </FormLabel>
              {viewOnly ? (
                <div>{expense!.notes || "No notes"}</div>
              ) : (
                <FormControl>
                  <Textarea
                    placeholder="e.g. Car rented in Florida"
                    {...field}
                  />
                </FormControl>
              )}
            </FormItem>
          )}
        />

        {/* Person who will review */}
        {expense?.status === StipendExpenseStatus.Pending && (
          <FormItem>
            <FormLabel>Designated Reviewer</FormLabel>
            <Body size="md" weight="regular">
              {expense.designatedApprover?.displayName ?? "Any Admin"}
            </Body>
          </FormItem>
        )}

        {/* Reviewed by */}
        {expense?.approvedOrRejectedBy?.displayName && (
          <FormItem>
            <FormLabel>Reviewed By</FormLabel>
            <Body size="md" weight="regular">
              {expense.approvedOrRejectedBy?.displayName}
            </Body>
          </FormItem>
        )}

        {/* Reviewer note */}
        {expense?.employerNotes && (
          <FormItem>
            <FormLabel>
              {expense.isAutoApproved
                ? "Auto-Approval Reason"
                : "Employer Notes"}
            </FormLabel>
            <Body size="md" weight="regular">
              {expense.employerNotes}
            </Body>
          </FormItem>
        )}

        <div className="flex-1" />

        {/* Submit button */}
        {!viewOnly && (
          <Button type="submit">
            {isSubmitting ? (
              <CircleNotch className="size-4 animate-spin" />
            ) : (
              "Submit Claim"
            )}
          </Button>
        )}
      </form>
    </Form>
  );
};
